/**
 * Analyseur de contenu pour le maillage interne automatique
 */

interface PageAnalysis {
  keywords: string[];
  entities: string[];
  topics: string[];
  sentiment: 'positive' | 'neutral' | 'negative';
  readabilityScore: number;
}

interface LinkOpportunity {
  keyword: string;
  context: string;
  suggestedUrl: string;
  anchor: string;
  confidence: number;
}

// Dictionnaire de mots-clés par catégorie pour l'entreprise de location de bennes
const SERVICE_KEYWORDS = {
  // Services principaux
  'location-bennes': [
    'location de bennes', 'location benne', 'bennes à louer', 'louer une benne',
    'location de contenants', 'contenants déchets', 'évacuation déchets'
  ],
  'demolition': [
    'démolition', 'démolir', 'destruction', 'abattage', 'déconstruction',
    'démolition maison', 'démolition bâtiment'
  ],
  'debarras': [
    'débarras', 'débarrasser', 'évacuation mobilier', 'nettoyage',
    'vidage maison', 'débarras appartement'
  ],
  'terrassement': [
    'terrassement', 'excavation', 'nivellement', 'préparation terrain',
    'travaux de terre'
  ],
  'achat-metaux': [
    'achat de métaux', 'rachat métaux', 'vendre métaux', 'rachat ferraille', 'prix ferraille',
    'rachat cuivre', 'vendre cuivre', 'rachat aluminium', 'rachat inox', 'rachat laiton',
    'métaux ferreux', 'métaux non ferreux', 'non-ferreux', 'non ferreux'
  ],
  
  // Types de déchets
  'dechets': [
    'déchets verts', 'gravats', 'déchets industriels', 'déchets de chantier',
    'déchets ménagers', 'encombrants'
  ],
  
  // Zones géographiques principales
  'occitanie': ['Occitanie'],
  'auvergne': ['Auvergne', 'Puy-de-Dôme', 'Clermont-Ferrand'],
  'ile-de-france': ['Île-de-France', 'IDF', 'Val-d\'Oise', 'Val d\'Oise', '95', 'Paris'],
  'aveyron': ['Aveyron', 'Rodez', 'Millau', 'Villefranche-de-Rouergue'],
  'toulouse': ['Toulouse', 'Haute-Garonne', '31', 'région toulousaine'],
  'tarn': ['Tarn', 'Albi', 'Castres', 'Gaillac'],
  'lot': ['Lot', 'Cahors', 'Figeac'],
  'lozere': ['Lozère', 'Mende', 'Florac'],
  'herault': ['Hérault', 'Montpellier', 'Béziers'],
  'tarn-et-garonne': ['Tarn-et-Garonne', 'Montauban', 'Castelsarrasin']
};

// URL de destination par catégorie
const CATEGORY_URLS = {
  'location-bennes': '/nos-services/location-bennes',
  'demolition': '/nos-services/demolition',
  'debarras': '/nos-services',
  'terrassement': '/nos-services',
  'achat-metaux': '/nos-services/achat-metaux',
  'dechets': '/valorisation',
  // Zones
  'occitanie': '/sites',
  'auvergne': '/sites',
  'ile-de-france': '/sites',
  // Départements (redirigés vers /sites?dept=)
  'aveyron': '/sites?dept=12',
  'toulouse': '/sites?dept=31', // Haute-Garonne
  'tarn': '/sites?dept=81',
  'lot': '/sites?dept=46',
  'lozere': '/sites?dept=48',
  'herault': '/sites?dept=34',
  'tarn-et-garonne': '/sites?dept=82'
};

/**
 * Analyse une page pour identifier les mots-clés et entités
 */
export const analyzePage = (pathname: string): PageAnalysis => {
  // Obtenir le contenu de la page actuelle
  const content = document.body.textContent || '';
  
  // Extraire les mots-clés principaux
  const keywords = extractKeywords(content);
  
  // Identifier les entités géographiques et services
  const entities = identifyEntities(content);
  
  // Déterminer les topics principaux
  const topics = identifyTopics(pathname, content);
  
  return {
    keywords,
    entities,
    topics,
    sentiment: 'neutral', // Simplified for now
    readabilityScore: calculateReadabilityScore(content)
  };
};

/**
 * Extrait les mots-clés principaux du contenu
 */
const extractKeywords = (content: string): string[] => {
  const words = content.toLowerCase()
    .replace(/[^\w\s-]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3);
  
  // Compter la fréquence des mots
  const wordCount = words.reduce((acc, word) => {
    acc[word] = (acc[word] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Retourner les mots les plus fréquents
  return Object.entries(wordCount)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 20)
    .map(([word]) => word);
};

/**
 * Identifie les entités nommées dans le contenu
 */
const identifyEntities = (content: string): string[] => {
  const entities: string[] = [];
  const lowerContent = content.toLowerCase();
  
  // Chercher les entités géographiques et services
  Object.entries(SERVICE_KEYWORDS).forEach(([category, keywords]) => {
    keywords.forEach(keyword => {
      if (lowerContent.includes(keyword.toLowerCase())) {
        entities.push(keyword);
      }
    });
  });
  
  return [...new Set(entities)];
};

/**
 * Identifie les topics principaux basés sur l'URL et le contenu
 */
const identifyTopics = (pathname: string, content: string): string[] => {
  const topics: string[] = [];
  
  // Analyser l'URL pour identifier le topic principal
  if (pathname.includes('location')) topics.push('location-bennes');
  if (pathname.includes('demolition')) topics.push('demolition');
  if (pathname.includes('debarras')) topics.push('debarras');
  if (pathname.includes('terrassement')) topics.push('terrassement');
  
  // Analyser le contenu pour identifier des topics secondaires
  const lowerContent = content.toLowerCase();
  Object.keys(SERVICE_KEYWORDS).forEach(category => {
    const keywords = SERVICE_KEYWORDS[category as keyof typeof SERVICE_KEYWORDS];
    const mentions = keywords.filter(keyword => 
      lowerContent.includes(keyword.toLowerCase())
    ).length;
    
    if (mentions > 0) {
      topics.push(category);
    }
  });
  
  return [...new Set(topics)];
};

/**
 * Calcule un score de lisibilité simple
 */
const calculateReadabilityScore = (content: string): number => {
  const words = content.split(/\s+/).length;
  const sentences = content.split(/[.!?]+/).length;
  const avgWordsPerSentence = words / sentences;
  
  // Score simplifié (plus c'est bas, plus c'est lisible)
  return Math.min(100, Math.max(0, 100 - (avgWordsPerSentence - 15) * 2));
};

/**
 * Trouve des opportunités de liens dans un texte
 */
export const findLinkOpportunities = (text: string, currentPath: string): LinkOpportunity[] => {
  const opportunities: LinkOpportunity[] = [];
  const lowerText = text.toLowerCase();
  
  Object.entries(SERVICE_KEYWORDS).forEach(([category, keywords]) => {
    const targetUrl = CATEGORY_URLS[category as keyof typeof CATEGORY_URLS];
    let adjustedUrl = targetUrl;

    // Ajuster dynamiquement pour "achat-metaux" lorsqu'une mention IDF est détectée
    if (category === 'achat-metaux') {
      const ileKeys = (SERVICE_KEYWORDS as any)['ile-de-france'] as string[] | undefined;
      if (ileKeys && ileKeys.some(k => lowerText.includes(k.toLowerCase()))) {
        adjustedUrl = '/nos-services/achat-metaux?region=ile-de-france';
      }
    }
    
    // Éviter les liens vers la page actuelle
    if (currentPath === adjustedUrl) return;
    
    keywords.forEach(keyword => {
      const keywordLower = keyword.toLowerCase();
      const index = lowerText.indexOf(keywordLower);
      
      if (index !== -1) {
        // Extraire le contexte autour du mot-clé
        const contextStart = Math.max(0, index - 50);
        const contextEnd = Math.min(text.length, index + keyword.length + 50);
        const context = text.substring(contextStart, contextEnd);
        
        opportunities.push({
          keyword: keywordLower,
          context,
          suggestedUrl: adjustedUrl,
          anchor: keyword,
          confidence: calculateConfidence(keyword, context, category)
        });
      }
    });
  });
  
  // Trier par confiance et retourner les meilleures opportunités
  return opportunities
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 10);
};

/**
 * Calcule la confiance pour une opportunité de lien
 */
const calculateConfidence = (keyword: string, context: string, category: string): number => {
  let confidence = 0.5; // Base confidence
  
  // Augmenter la confiance si le mot-clé est exact
  if (context.toLowerCase().includes(keyword.toLowerCase())) {
    confidence += 0.2;
  }
  
  // Accent fort sur services prioritaires
  if (['location-bennes', 'achat-metaux'].includes(category)) {
    confidence += 0.25;
  }

  // Bonus si le contexte mentionne une région clé + service prioritaire
  const ctx = context.toLowerCase();
  if (['location-bennes', 'achat-metaux'].includes(category) && /occitanie|auvergne|île-de-france|ile-de-france|idf|paris|val d[’' -]?oise/.test(ctx)) {
    confidence += 0.15;
  }
  
  // Diminuer la confiance si le contexte contient des négations
  if (/\b(pas|non|sans|aucun|jamais)\b/.test(ctx)) {
    confidence -= 0.3;
  }
  
  return Math.min(1, Math.max(0, confidence));
};

/**
 * Génère des ancres alternatives pour un mot-clé
 */
export const generateAlternativeAnchors = (keyword: string, category: string): string[] => {
  const anchors = [keyword];
  
  switch (category) {
    case 'location-bennes':
      anchors.push('nos services de location', 'location de contenants', 'services de bennes');
      break;
    case 'demolition':
      anchors.push('services de démolition', 'travaux de démolition', 'démolition professionnelle');
      break;
    case 'debarras':
      anchors.push('services de débarras', 'évacuation mobilier', 'nettoyage professionnel');
      break;
    default:
      anchors.push(`services ${keyword}`, `solutions ${keyword}`);
  }
  
  return anchors.slice(0, 3);
};